/**
 * 通知センター — the 治療院 portal's copy. Two tabs, one clinic.
 *
 * SCOPE IS SERVER-SIDE. No clinic id is ever sent from here: every endpoint reads it off the authenticated
 * user (ClinicNotificationCenterController::clinicId). The tenant and clinic number in the path are the
 * portal's routing convention, not the scope — sending someone else's would fail auth, not widen the list.
 *
 * NO MOCK BRANCH, unlike the admin copy. Both tabs this screen has were live before it existed.
 *
 * The rows come back in the SAME wire shape as the admin screen's and go through the SAME adapter, so a
 * change to what a row means lands on both screens at once. What differs is the request (no clinic_number /
 * clinic_name) and the tab set.
 */
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { parseFilenameFromHeader } from '@/utils/blobDownload';
import {
  toNotificationItem,
  type RawNotification,
} from '@/services/admin/notification-center/adapter';
import {
  DEFAULT_PER_PAGE,
  type NotificationBulkResult,
  type NotificationListQuery,
  type NotificationListResult,
  type NotificationUnreadCounts,
} from '@/types/admin-notification-center';

const BASE_PATH = '/notification-center';

/**
 * meta — snake_case on the wire, camelCase in the app. THE PAGER DEPENDS ON THIS.
 *
 * Kept identical to the admin copy on purpose (services/admin/notification-center/index.ts documents the
 * bug it fixes: a mismatch here leaves antd's Pagination highlighting page 1 for ever, and clicking 「1」
 * from page 2 becomes a no-op — the screen is then stuck with no way back).
 */
const toMeta = (raw: Record<string, unknown> | undefined | null): NotificationListResult['meta'] => {
  const pick = (...names: string[]): number => {
    for (const name of names) {
      const value = raw?.[name];
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }

    return 0;
  };

  const perPage = pick('perPage', 'per_page') || DEFAULT_PER_PAGE;
  const total = pick('total');

  return {
    currentPage: pick('currentPage', 'current_page') || 1,
    perPage,
    total,
    lastPage: pick('lastPage', 'last_page') || Math.max(1, Math.ceil(total / perPage)),
  };
};

/**
 * Flat query string.
 *
 * 治療院ID / 治療院名 ARE NOT SENT. The screen has no such control — the reader is inside one clinic — and
 * the server does not read them on this route either. Both halves matter: a filter that only the client
 * drops is one hand-typed URL away from being honoured again.
 */
const toParams = (q: NotificationListQuery) => ({
  tab: q.tab,
  page: q.page,
  per_page: q.perPage,
  from: q.from || undefined,
  to: q.to || undefined,
  read: q.read === 'all' ? undefined : q.read,
  levels: q.levels.length ? q.levels : undefined,
  // Still an ARRAY on the wire even though the control now yields one value: the server reads
  // service_types as an array and intersects it against ['A2','R2'], so a scalar would arrive as an
  // empty intersection and silently return nothing.
  service_types: q.serviceType ? [q.serviceType] : undefined,
  patient: q.patient.trim() || undefined,
  // 種別 on 審査システムからの通知 — same contract as the admin screen's.
  kinds: q.kinds.length ? q.kinds : undefined,
});

export const fetchClinicNotificationList = async (
  siteType: string,
  clinicNumber: string,
  query: NotificationListQuery
): Promise<NotificationListResult> => {
  const response = await apiClient.get<{
    data: {
      data: RawNotification[];
      meta: Record<string, unknown>;
      summary: NotificationListResult['summary'];
    };
  }>(clinicApiPath(siteType, clinicNumber, BASE_PATH), { params: toParams(query) });

  const payload = response.data.data;

  return {
    data: payload.data.map(toNotificationItem),
    meta: toMeta(payload.meta),
    summary: payload.summary,
  };
};

/**
 * Tab badges. Both keys always present, zeros included.
 *
 * An ABSENT key means 「no answer」 to the screen, which is how the admin badge once showed a sample number
 * over a real list. There is no sample data here, so the two keys are simply always sent.
 */
export const fetchClinicNotificationUnreadCounts = async (
  siteType: string,
  clinicNumber: string
): Promise<Pick<NotificationUnreadCounts, 'rezept' | 'inbound'>> => {
  const response = await apiClient.get<{ data: { rezept?: number; inbound?: number } }>(
    clinicApiPath(siteType, clinicNumber, `${BASE_PATH}/unread-count`)
  );

  return {
    rezept: response.data.data.rezept ?? 0,
    inbound: response.data.data.inbound ?? 0,
  };
};

/**
 * Both bulk actions take an ID LIST, not a filter — the screen can only send ids it has drawn, so a press
 * can never reach a row the operator has not seen.
 */
export const markClinicNotificationsRead = async (
  siteType: string,
  clinicNumber: string,
  ids: number[]
): Promise<NotificationBulkResult> => {
  const response = await apiClient.post<{ data: NotificationBulkResult }>(
    clinicApiPath(siteType, clinicNumber, `${BASE_PATH}/mark-read`),
    { ids }
  );

  return response.data.data;
};

export const retryClinicNotifications = async (
  siteType: string,
  clinicNumber: string,
  ids: number[]
): Promise<NotificationBulkResult> => {
  const response = await apiClient.post<{ data: NotificationBulkResult }>(
    clinicApiPath(siteType, clinicNumber, `${BASE_PATH}/retry`),
    { ids }
  );

  return response.data.data;
};

/**
 * ① エラー詳細 — FETCHED WITH THE API CLIENT rather than opened as a link: the endpoint is behind the bearer
 * token, and an <a href> carries no Authorization header, so it would land on the login screen.
 *
 * THE SERVER'S NAME WINS — BD §CSV 8.0: 「RAH_送信エラー_{治療院ID}_{提出年月}_{YYYYMMDD-HHmm}.csv」, the same
 * name the admin portal's copy of this file carries. `sync_errors_{通知ID}.csv` is the fallback the BD names
 * for the case where the browser receives no name from the server.
 */
export const downloadClinicRunErrorCsv = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get<Blob>(
    clinicApiPath(siteType, clinicNumber, `${BASE_PATH}/${id}/error-csv`),
    { responseType: 'blob' }
  );

  return {
    blob: response.data,
    filename:
      parseFilenameFromHeader(response.headers?.['content-disposition']) ?? `sync_errors_${id}.csv`,
  };
};

/**
 * ② 不一致明細 — the rezepts behind one 状態の不一致 notice, for THIS clinic.
 *
 * A DIFFERENT FILE FROM THE ERROR CSV, and a different question. That one answers 「which rezepts of this
 * 確定 press failed to send」 out of the audit trail; this answers 「which rezepts do RAH and 審査 disagree
 * about」, and it was written to disk by the nightly reconcile that raised the notice — the download only
 * reads back what the run froze, so the file and the count in the notice cannot drift apart.
 *
 * ONE CLINIC, NEVER THE SITE BUNDLE. The reconcile run writes one CSV per clinic and the notice this
 * endpoint reads names exactly one of them; the admin portal's ZIP of every clinic on a site stays on the
 * admin route. Scope is the server's — ClinicNotificationCenterController::driftCsv refuses any id this
 * clinic does not own, so nothing here narrows anything.
 */
export const downloadClinicDriftCsv = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get<Blob>(
    clinicApiPath(siteType, clinicNumber, `${BASE_PATH}/${id}/drift-csv`),
    { responseType: 'blob' }
  );

  return {
    blob: response.data,
    filename:
      parseFilenameFromHeader(response.headers?.['content-disposition']) ??
      `reconcile_drift_${id}.csv`,
  };
};
